import { useMutation, useQuery } from '@tanstack/react-query';
import { api } from '@/api/client';
import { queryClient, queryKeys } from '@/api/query-client';
import type { components } from '@/api/generated/schema';

type Schemas = components['schemas'];

export type ScheduleBroadcastInput = Schemas['PixlideApplicationFeaturesBroadcastsScheduleBroadcastCommand'];
export type RescheduleBroadcastInput = Schemas['PixlideApplicationFeaturesBroadcastsRescheduleBroadcastCommand'];
export type BroadcastTransition = Schemas['PixlideApplicationFeaturesBroadcastsBroadcastTransition'];

/**
 * Une diffusion modifie aussi le compteur « diffusé sur N écrans » des vidéos : les deux
 * listes sont invalidées ensemble.
 */
function invalidateBroadcasts(): void {
  void queryClient.invalidateQueries({ queryKey: queryKeys.broadcasts() });
  void queryClient.invalidateQueries({ queryKey: queryKeys.projects() });
}

/** Détail d'une diffusion, avec ses écrans : sert à pré-remplir la modification. */
export function useBroadcast(broadcastId: string | null) {
  return useQuery({
    queryKey: queryKeys.broadcasts({ id: broadcastId }),
    enabled: broadcastId !== null,

    queryFn: async () => {
      const { data } = await api.GET('/api/v1/broadcasts/{broadcastId}', {
        params: { path: { broadcastId: broadcastId! } },
      });

      return data!;
    },
  });
}

/**
 * Programme une diffusion. Publiée immédiatement : les écrans visés sont prévenus en temps
 * réel par la passerelle de diffusion et la jouent dès que leur créneau s'ouvre.
 */
export function useScheduleBroadcast() {
  return useMutation({
    mutationFn: async (input: ScheduleBroadcastInput) => {
      const { data } = await api.POST('/api/v1/broadcasts', { body: input });

      return data!;
    },

    onSuccess: invalidateBroadcasts,
  });
}

export function useRescheduleBroadcast() {
  return useMutation({
    mutationFn: async ({ broadcastId, input }: { broadcastId: string; input: RescheduleBroadcastInput }) => {
      await api.PUT('/api/v1/broadcasts/{broadcastId}/schedule', {
        params: { path: { broadcastId } },
        body: input,
      });
    },

    onSuccess: invalidateBroadcasts,
  });
}

/** Mise en pause, reprise ou arrêt définitif d'une diffusion. */
export function useChangeBroadcastState() {
  return useMutation({
    mutationFn: async ({ broadcastId, transition }: { broadcastId: string; transition: BroadcastTransition }) => {
      await api.POST('/api/v1/broadcasts/{broadcastId}/state', {
        params: { path: { broadcastId } },
        body: { transition },
      });
    },

    onSuccess: invalidateBroadcasts,
  });
}
